import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useUserInformation } from "./queries/useUserInformation";
import { useUserCompanies } from "./queries/useUserCompanies";
import { useUpdateSelectedCompany } from "./mutations/useUpdateSelectedCompany";
import { useAppStore } from "../stores/useAppStore";
import { CompanyUser } from "../supabase/types";

export function useAppInitialization() {
  const queryClient = useQueryClient();

  // Queries
  const { data: userInfo, isLoading: isLoadingUser } = useUserInformation();
  const { data: companiesData, isLoading: isLoadingCompanies } = useUserCompanies(userInfo);

  // Mutations
  const updateSelectedCompany = useUpdateSelectedCompany();

  // Zustand store
  const {
    selectedCompanyId,
    selectedCompanyUser,
    setSelectedCompany,
    setInitialized,
  } = useAppStore();

  // Initialize app when user data is loaded
  useEffect(() => {
    if (!isLoadingUser && !isLoadingCompanies) {
      if (userInfo && companiesData) {
        // Find the previously selected company or the first verified one
        const previouslySelectedId = userInfo.previously_selected_company_id;
        const newSelectedCompanyUser = companiesData.companyUsers.find(
          (company: CompanyUser) =>
            company.is_verified &&
            !company.deleted_at &&
            (company.company_id === previouslySelectedId || !previouslySelectedId)
        );
        if (newSelectedCompanyUser && (newSelectedCompanyUser.company_id !== selectedCompanyId || JSON.stringify(newSelectedCompanyUser) !== JSON.stringify(selectedCompanyUser))) {
          setSelectedCompany(newSelectedCompanyUser.company_id, newSelectedCompanyUser);
        }
      }
      setInitialized(true);
    }
  }, [isLoadingUser, isLoadingCompanies, userInfo, companiesData, selectedCompanyId, setSelectedCompany, setInitialized, selectedCompanyUser]);

  // Function to switch companies
  const switchCompany = async (companyId: string) => {
    if (!userInfo || !companiesData) return;

    const newCompany = companiesData.companyUsers.find(
      (c: CompanyUser) => c.company_id === companyId && c.is_verified && !c.deleted_at
    );

    if (!newCompany) {
      throw new Error("Cannot select unverified company");
    }

    // Clear all queries cache when switching companies
    queryClient.clear();

    // Update local state immediately
    setSelectedCompany(companyId, newCompany);

    // Update in database
    await updateSelectedCompany.mutateAsync({
      userId: userInfo.id,
      companyId: companyId,
    });
  };

  return {
    userInfo,
    userCompanies: companiesData?.companyUsers,
    companiesInfo: companiesData?.companies,
    selectedCompany: companiesData?.companyUsers.find((c: CompanyUser) => c.company_id === selectedCompanyId),
    isLoading: isLoadingUser || isLoadingCompanies,
    switchCompany,
  };
}